import { createSelector } from '@reduxjs/toolkit';
import { TRANSACTION_PAGE_TABS } from 'config';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { withCanonicalLookups } from 'utils/gridColumnLookups';
import { handleHiddenCameras } from './utils/handleHiddenCameras';
import { getGroupKey } from './transactionsSlice';
import { selectIsTransactionSubStreamEnabled } from 'store/featureFlag/featureFlagSelector';
import { getSubStreamCameraIds } from 'utils/transactionSubStream';

const emptyArray = [];
const emptyObject = {};

const {
    VIDEO_WALL_TAB: { ID: videoWallTabId, DUMMY_TRANSACTION_ID: dummyTransactionId },
    REOPENED_TRANSACTION_TAB: { ID: reopenedTransactionTabId },
} = TRANSACTION_PAGE_TABS;

const selectTransactionsSlice = (state) => state.transactions;

export const selectActiveTab = createSelector([selectTransactionsSlice], (transactiosSlice) => transactiosSlice.activeTab);
export const selectVisitedTransactionTabIds = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.visitedTransactionTabIds ?? emptyObject
);
export const createIsTabActiveSelectorByTabId = (tabId) =>
    createSelector([selectTransactionsSlice, (_, id = tabId) => id], (transactionSlice, tabId) => transactionSlice?.activeTab === tabId);

export const activeTabHasStreams = createSelector([selectTransactionsSlice, selectActiveTab], (transaactionSlice, activeTab) => {
    if (typeof activeTab === 'number') return true; //meaninng transaction tab is active
    if (activeTab === reopenedTransactionTabId) return true;
    if (activeTab === videoWallTabId) return !!transaactionSlice.videoWallCameras?.level1?.length;
    return false;
});

export const selectTabsAudioConfig = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.tabsAudio);

export const selectActiveTalkUrl = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.activeTalkUrl);

export const selectTalkSessionStarting = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.talkSessionStarting);

export const selectActiveDemoTalkUrl = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.activeDemoTalkUrl);
export const selectTalkSessionPaused = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.talkSessionPaused);
export const selectSharedWorker = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.sharedWorker);

export const selectStreamingCameraTotal = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.sharedWorker?.streamingCameraTotal ?? 0
);

export const selectTabAudioConfigByTabId = (tabId) =>
    createSelector([selectTabsAudioConfig, (_, id = tabId) => id], (tabAudioOffObj, id) => tabAudioOffObj?.[id]);

export const selectTransactions = createSelector([selectTransactionsSlice], (transactiosSlice) => transactiosSlice.transactions);

export const selectChangingOperationalModeInProgress = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.changingOperationalModeInProgress
);
export const selectIsReceivingTransactionsEnabled = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.isReceivingTransactionsEnabled
);

export const selectIsVideoWallEnabled = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.isVideoWallEnabled);
export const selectVideoWallCameras = createSelector([selectTransactionsSlice], (transactiosSlice) => transactiosSlice.videoWallCameras);
export const selectIsLoadingRealTimeStreams = createSelector(
    [selectTransactionsSlice],
    (transactiosSlice) => transactiosSlice.isLoadingRealTimeStreams
);

export const selectIsShowNewCameras = createSelector([selectTransactionsSlice], (transactiosSlice) => transactiosSlice.showNewCameras);
export const selectDummyVideoWallTransaction = createSelector([selectVideoWallCameras], (camsConfig) => {
    return {
        tabId: videoWallTabId,
        id: dummyTransactionId,
        camsConfig,
    };
});
export const selectFetchingVideoWallCamerasInProgress = createSelector(
    [selectTransactionsSlice],
    (transactiosSlice) => transactiosSlice.fetchingVideoWallCamerasInProgress
);

export const selectUpdatingTransactionInProgress = createSelector(
    [selectTransactionsSlice],
    (transactiosSlice) => transactiosSlice.updatingTransactionInProgress
);

export const selectShowRealTimeUpdateMessages = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.showRealTimeUpdateMessages
);

export const selectHasReopenedTransaction = createSelector([selectTransactionsSlice], (transactiosSlice) => transactiosSlice.hasReopenedTransaction);
export const selectLastClosedTransasctionId = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.lastClosedTransactionId
);

export const selectReopenTransaction = createSelector([selectTransactionsSlice], (transactionsSlice) => transactionsSlice.reopenTransaction);

export const selectAgentReviewByTransactionId = (transactionId) =>
    createSelector([selectTransactions], (transactions) => {
        const transaction = transactions.find((t) => t.id === transactionId);
        return transaction?.agentReview ?? null;
    });

export const selectStompBrokerConnectionState = createSelector(
    [selectTransactionsSlice],
    (transactiosSlice) => transactiosSlice.stompBrokerConnectionState
);

export const selectActiveTransactionId = createSelector([selectTransactions, selectActiveTab], (transactions, activeTab) => {
    let transactionId = '';
    for (let transaction of transactions) {
        if (transaction.tabId === activeTab) {
            transactionId = transaction.id;
        }
    }

    return transactionId;
});

export const selectTranasctionById = (id) =>
    createSelector([selectTransactions, (_, transactionId = id) => transactionId], (allTransactions, transactionId) => {
        return allTransactions.find((transaction) => {
            return transaction.id === transactionId;
        });
    });

export const createTransactionsByGateIdCountSelector = (gateId) =>
    createSelector(
        [selectTransactions, (_, id = gateId) => id],
        (allTransactions, gateId) => allTransactions?.filter((transaction) => transaction?.gateId === gateId)?.length || 0
    );

export const selectRequestFullScreen = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.requestFullScreenForTabId);

export const selectLocationIdByTransactionId = (id) =>
    createSelector([selectTranasctionById(id)], (transaction) => {
        return transaction.locationId;
    });

// A camera can come as several entries (MAIN + SUB, on the video wall and with transactionSubStreamConfigEnabled):
// every entry creates its player, only the first entry of each camera (MAIN) is rendered. `isRendered` leaves entries
// out of the rendered list only (e.g. hidden video wall cameras keep their player).
function createPlayersKeepFirstPerCamera(entries, isRendered) {
    const rendered = [];
    const gateCameraIds = new Set();
    for (const camConfig of entries) {
        cameraStreamManager.create(camConfig);
        if ((isRendered && !isRendered(camConfig)) || gateCameraIds.has(camConfig.gateCameraId)) continue;
        gateCameraIds.add(camConfig.gateCameraId);
        rendered.push(camConfig);
    }
    return rendered;
}

export const selectTransactionCamerasByTransactionId = (id) => {
    let selectorFn;
    const isVideoWall = id === TRANSACTION_PAGE_TABS.VIDEO_WALL_TAB.DUMMY_TRANSACTION_ID;
    if (!isVideoWall) selectorFn = selectTranasctionById(id);
    if (isVideoWall) selectorFn = selectVideoWallCameras;

    return createSelector([selectorFn, selectCompactVideoWallMode], (object) => {
        const level1 = (isVideoWall ? object.level1 : object.camsConfig.level1) || emptyArray;
        const level2 = (isVideoWall ? object.level2 : object.camsConfig.level2) || emptyArray;

        handleHiddenCameras(level1, id);

        const configL1 = createPlayersKeepFirstPerCamera(level1, (camConfig) => !camConfig.hidden);
        const configL2 = createPlayersKeepFirstPerCamera(level2);
        return { configL1, configL2 };
    });
};

export const reportIssuesModalState = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.reportIssuesModalState);
export const selectedCameraIssues = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.selectedCameraIssues);
export const selectedIssueUrl = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.selectedIssueUrl);
export const issueTitle = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.issueTitle);
export const issueDataLoggs = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.issueDataLoggs);

export const selectVideoWallTransactionNotifications = createSelector(
    [selectTransactionsSlice],
    (transactiosSlice) => transactiosSlice.videoWallTransactionNotifications
);
export const selectVideoWallFinishedTransactionIds = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.videoWallFinishedTransactionIds ?? emptyObject
);

export const selectIsImageDigitalZoomed = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.isImageDigitalZoomed);
export const selectCompactVideoWallMode = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.compactVideoWallMode);
export const selectVideoWallCollapsedCameraIds = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.videoWallCollapsedCameraIds
);
export const selectVideoWalls = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.videoWalls);
export const selectCircleCheckActive = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.circleCheckActive);
export const selectCameraStreamWindow = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.cameraStreamWindow);
export const selectCollapsedCompactSortedType = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.collapsedCompactSortedType
);
export const selectCollapsedCompactSortDirections = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.collapsedCompactSortDirections
);
export const selectShowCollapsedSortInfo = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.showCollapsedSortInfo);
export const selectVideoWallCamerasRealTimeUpdated = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.videoWallCamerasRealTimeUpdated
);

export const selectTransactionsSplitViewMode = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.splitViewMode);
export const selectLaneArrivalOrder = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.laneArrivalOrder);
export const selectPinnedLanes = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.pinnedLanes);
export const selectMaxPinnedLanes = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.maxPinnedLanes);
// Per-gateId selectors are cached so repeated calls return a stable reference (memoization survives renders).
// The gate set is bounded per desk session, so the cache is not evicted.
const isLanePinnedSelectorCache = new Map();
export const selectIsLanePinned = (gateId) => {
    if (!isLanePinnedSelectorCache.has(gateId)) {
        isLanePinnedSelectorCache.set(
            gateId,
            createSelector([selectPinnedLanes], (pinnedLanes) => pinnedLanes.includes(gateId))
        );
    }
    return isLanePinnedSelectorCache.get(gateId);
};
export const selectLaneActionInProgress = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.laneActionInProgress);
const laneActionInProgressSelectorCache = new Map();
export const selectIsLaneActionInProgress = (gateId) => {
    if (!laneActionInProgressSelectorCache.has(gateId)) {
        laneActionInProgressSelectorCache.set(
            gateId,
            createSelector([selectLaneActionInProgress], (laneActionInProgress) => !!laneActionInProgress[gateId])
        );
    }
    return laneActionInProgressSelectorCache.get(gateId);
};
const videoWallLaneReceivedAtSelectorCache = new Map();
// When this browser received the lane's detection (see the detection handler in transactionsSagas).
export const selectVideoWallLaneReceivedAt = (gateId) => {
    if (!videoWallLaneReceivedAtSelectorCache.has(gateId)) {
        videoWallLaneReceivedAtSelectorCache.set(
            gateId,
            createSelector([selectVideoWallTransactionNotifications], (notifications) => notifications?.[gateId]?.receivedAt ?? null)
        );
    }
    return videoWallLaneReceivedAtSelectorCache.get(gateId);
};
export const selectVideoWallPinnedLanes = createSelector([selectTransactionsSlice], (transactionSlice) => transactionSlice.videoWallPinnedLanes);
const videoWallPinnedLaneUsernameSelectorCache = new Map();
export const selectVideoWallPinnedLaneUsername = (gateId) => {
    if (!videoWallPinnedLaneUsernameSelectorCache.has(gateId)) {
        videoWallPinnedLaneUsernameSelectorCache.set(
            gateId,
            createSelector([selectVideoWallPinnedLanes], (videoWallPinnedLanes) => videoWallPinnedLanes[gateId]?.accountUsername ?? null)
        );
    }
    return videoWallPinnedLaneUsernameSelectorCache.get(gateId);
};
const videoWallPinnedLaneTimestampSelectorCache = new Map();
export const selectVideoWallPinnedLaneTimestamp = (gateId) => {
    if (!videoWallPinnedLaneTimestampSelectorCache.has(gateId)) {
        videoWallPinnedLaneTimestampSelectorCache.set(
            gateId,
            createSelector([selectVideoWallPinnedLanes], (videoWallPinnedLanes) => videoWallPinnedLanes[gateId]?.pinnedAt ?? null)
        );
    }
    return videoWallPinnedLaneTimestampSelectorCache.get(gateId);
};
export const selectSplitViewActiveTransactionId = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.splitViewActiveTransactionId
);
export const selectSplitViewActiveTransactionGroup = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.splitViewActiveTransactionGroup
);

export const selectGroupedTransactions = createSelector([selectTransactions, selectLaneArrivalOrder], (allTransactions, laneArrivalOrder) => {
    const groups = {};
    for (let i = 0; i < allTransactions.length; i++) {
        const txn = allTransactions[i];
        const groupKey = `${txn.locationId || txn.locationName}-${txn.gateDirection}-${txn.gateId || txn.gateName}`;

        if (!groups[groupKey]) {
            groups[groupKey] = {
                key: groupKey,
                locationName: txn.locationName,
                locationId: txn.locationId,
                gateName: txn.gateName,
                gateId: txn.gateId,
                gateDirection: txn.gateDirection,
                transactions: [],
            };
        }

        groups[groupKey].transactions.push(txn);
    }

    const result = Object.values(groups);

    for (let i = 0; i < result.length; i++) {
        result[i].transactions.sort((a, b) => {
            const timeA = a.timestamp?.received ? new Date(a.timestamp.received).getTime() : 0;
            const timeB = b.timestamp?.received ? new Date(b.timestamp.received).getTime() : 0;
            return timeA - timeB;
        });
    }

    const orderIndex = new Map(laneArrivalOrder.map((key, index) => [key, index]));
    result.sort((a, b) => (orderIndex.get(a.key) ?? Infinity) - (orderIndex.get(b.key) ?? Infinity));

    return result;
});

export const selectTransactionGroupByGroupId = (groupId) =>
    createSelector([selectGroupedTransactions, (_, id = groupId) => id], (groupedTransactions, transactionGroupId) => {
        const transactionGroup = groupedTransactions.find(({ key }) => key === transactionGroupId);
        return transactionGroup || emptyObject;
    });

export const selectTransactionCamerasByTransactionGroupId = (transactionGroupId) =>
    createSelector([selectTransactionGroupByGroupId(transactionGroupId)], (transactionGroup) => {
        const { transactions } = transactionGroup;
        const firstTransaction = transactions?.[0];
        if (!firstTransaction) return emptyArray;
        const camsConfig = firstTransaction?.camsConfig;
        const { level1 = emptyArray, level2 = emptyArray } = camsConfig || {};
        return createPlayersKeepFirstPerCamera([...level1, ...level2]).map((camConfig) => ({ ...camConfig, transactionGroupId }));
    });

export const createTransactionTabGroupsSelector = (maxDeskTransactions) =>
    createSelector([selectTransactions, selectLaneArrivalOrder], (allTransactions, laneArrivalOrder) => {
        let maxNumberOfTabs = maxDeskTransactions;

        const result = allTransactions.reduce(
            (acc, transaction) => {
                const { tabId, id, locationName, locationId, gateDirection, reopened, gateName, gateId, source, camsConfig, timestamp } = transaction;

                if (reopened) {
                    maxNumberOfTabs++;
                }

                const tabGroupKey = `${gateId}-${gateDirection}`;

                const date = timestamp?.received ? new Date(timestamp.received) : new Date();
                const time = new Intl.DateTimeFormat('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                }).format(date);

                const transactionData = {
                    tabId,
                    transactionId: id,
                    locationName,
                    locationId,
                    gateName,
                    gateId,
                    gateDirection,
                    source,
                    camsConfig,
                    reopened,
                    time,
                    title1: `${locationName} | ${gateName}`,
                    title2: `T:${id} | ${time}`,
                };

                if (reopened) {
                    acc.reopenedTransaction = {
                        tabGroupKey,
                        ...transactionData,
                        allTabIds: [tabId],
                        transactions: { [tabId]: transactionData },
                    };
                } else if (acc.tabGroups[tabGroupKey] !== undefined || Object.keys(acc.tabGroups).length < maxNumberOfTabs) {
                    if (!acc.tabGroups[tabGroupKey]) {
                        acc.tabGroups[tabGroupKey] = {
                            tabGroupKey,
                            ...transactionData,
                            allTabIds: [tabId],
                            transactions: { [tabId]: transactionData },
                        };
                    } else {
                        acc.tabGroups[tabGroupKey].allTabIds.push(tabId);
                        acc.tabGroups[tabGroupKey].transactions[tabId] = transactionData;
                    }
                }

                return acc;
            },
            {
                tabGroups: {},
                reopenedTransaction: null,
            }
        );

        const orderIndex = new Map(laneArrivalOrder.map((key, index) => [key, index]));
        const sortedEntries = Object.entries(result.tabGroups)
            .map((entry) => ({ entry, order: orderIndex.get(getGroupKey(entry[1])) ?? Infinity }))
            .sort((a, b) => a.order - b.order)
            .map(({ entry }) => entry);
        result.tabGroups = Object.fromEntries(sortedEntries);

        return result;
    });

export const selectSheetAnnouncementsByTransactionId = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.sheetAnnouncementsByTransactionId || emptyObject
);

export const createSheetAnnouncementsByTransactionIdSelector = (transactionId) =>
    createSelector([selectSheetAnnouncementsByTransactionId, (_, id = transactionId) => id], (sheetAnnouncementsByTransactionId, id) => {
        return sheetAnnouncementsByTransactionId?.[id] || emptyObject;
    });

// The stored config sits in the same object as the announcement rows and the status, so it has to be
// selected on its own first: otherwise every page of rows would rebuild the columns below and the
// grid would be handed a new config it never asked for.
const createStoredSheetAnnouncementsColumnsSelector = (transactionId) =>
    createSelector(
        [createSheetAnnouncementsByTransactionIdSelector(transactionId)],
        (sheetAnnouncements) => sheetAnnouncements?.columnsConfig || emptyArray
    );

// withCanonicalLookups shares one lookup instance per distinct list, which is what keeps the grid
// alive when it is handed a config it has not seen. The instance it hands over is a copy, so the
// patch DevExtreme performs does not write into frozen store state either. See
// utils/gridColumnLookups.
export const createSheetAnnouncementsColumnsConfigSelector = (transactionId) =>
    createSelector([createStoredSheetAnnouncementsColumnsSelector(transactionId)], withCanonicalLookups);

export const createSheetAnnouncementsTableDataSelector = (transactionId) =>
    createSelector(
        [createSheetAnnouncementsByTransactionIdSelector(transactionId)],
        (sheetAnnouncements) => sheetAnnouncements?.tableData || emptyArray
    );

export const createHasSheetAnnouncementsSelector = (transactionId) =>
    createSelector([createSheetAnnouncementsTableDataSelector(transactionId)], (tableData) => tableData?.length > 0);

export const createSheetAnnouncementsStatusSelector = (transactionId) =>
    createSelector([createSheetAnnouncementsByTransactionIdSelector(transactionId)], (sheetAnnouncements) => sheetAnnouncements?.status || 'SUCCESS');

export const createSheetAnnouncementsColorSelector = (transactionId) =>
    createSelector([createSheetAnnouncementsByTransactionIdSelector(transactionId)], (sheetAnnouncements) => sheetAnnouncements?.color ?? null);

export const createSheetAnnouncementsFirstRowSelector = (transactionId) =>
    createSelector([createSheetAnnouncementsByTransactionIdSelector(transactionId)], (sheetAnnouncements) => {
        const tableData = sheetAnnouncements?.tableData;
        const status = sheetAnnouncements?.status || 'SUCCESS';
        if (!tableData?.length) return null;
        return { data: tableData[0], status };
    });

export const createSheetAnnouncementFieldsSelector = (transactionId) =>
    createSelector([createSheetAnnouncementsFirstRowSelector(transactionId)], (firstRowData) => {
        if (!firstRowData) return emptyObject;

        const { data, status } = firstRowData;
        const result = {};

        const extractFields = (obj, prefix = '') => {
            if (!obj || typeof obj !== 'object') return;

            Object.keys(obj).forEach((key) => {
                const fieldPath = prefix ? `${prefix}.${key}` : key;
                const value = obj[key];

                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    extractFields(value, fieldPath);
                } else if (value != null && value !== '' && key !== 'id') {
                    result[fieldPath] = { value, status };
                }
            });
        };

        extractFields(data);
        return result;
    });

export const selectEditTransactionModal = createSelector([selectTransactionsSlice], (formsSlice) => formsSlice.editTransactionModal);
export const selectEditTransactionModalDelete = createSelector([selectTransactionsSlice], (formsSlice) => formsSlice.editTransactionModalDelete);

export const selectSheetAnnouncementPendingGate = createSelector(
    [selectTransactionsSlice],
    (transactionSlice) => transactionSlice.sheetAnnouncementPendingGate || emptyObject
);

// Cameras the open transactions play as SUB (transactionSubStreamConfigEnabled): AoT warm-up keeps them on the stream
// type they already play. Empty while the flag is off, so AoT behaves as before.
export const selectSubStreamCameraIds = createSelector([selectIsTransactionSubStreamEnabled, selectTransactions], (isEnabled, transactions) =>
    isEnabled ? getSubStreamCameraIds(transactions) : new Set()
);
